import { useState } from 'react';
import { Link } from 'react-router-dom';
import { EnvelopeSimple, Spinner, CheckCircle, ArrowLeft } from '@phosphor-icons/react';
import API_URL from '../utils/apiUrl';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setSent(true);
      } else {
        // Try JSON first; fall back to text so we can diagnose non-JSON 404/502s
        let detail = '';
        try {
          const data = await res.json();
          detail = typeof data.detail === 'string' ? data.detail : '';
        } catch {
          try { detail = (await res.text()).slice(0, 120); } catch { /* ignore */ }
        }
        setError(`Couldn't send the reset link (HTTP ${res.status}).${detail ? ' ' + detail : ''} Try again in a moment, or contact hello@grade9ready.com if it keeps happening.`);
      }
    } catch (err) {
      setError(`Could not reach the server. Check your connection and try again. (${err?.message || 'network error'})`);
    }
    setLoading(false);
  };

  if (sent) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
        <div className="neo-card max-w-md w-full text-center p-8">
          <div className="w-16 h-16 bg-emerald-500 rounded-full border-2 border-foreground flex items-center justify-center mx-auto mb-4 shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]">
            <CheckCircle size={32} weight="fill" className="text-white" />
          </div>
          <h2 className="text-2xl font-heading font-bold mb-2">Check your email</h2>
          <p className="text-muted-foreground mb-2">If an account exists for <strong>{email}</strong>, we've sent a password reset link.</p>
          <p className="text-sm text-muted-foreground mb-6">The link expires in 1 hour. Check your spam folder if you don't see it.</p>
          <Link to="/login" className="neo-btn inline-block" data-testid="back-to-login">Back to Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
      <div className="neo-card max-w-md w-full p-8" data-testid="forgot-password-page">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-6">
          <ArrowLeft size={14} /> Back to Login
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-primary rounded-xl border-2 border-foreground flex items-center justify-center shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]">
            <EnvelopeSimple size={24} weight="fill" className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-heading font-bold">Forgot your password?</h1>
            <p className="text-sm text-muted-foreground">We'll send you a reset link</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-bold mb-1">Email address</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="neo-input w-full"
              placeholder="your@email.com"
              required
              data-testid="forgot-email"
            />
          </div>

          {error && <p className="text-destructive text-sm" data-testid="forgot-error">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="neo-btn-primary w-full !py-3 font-bold flex items-center justify-center gap-2"
            data-testid="forgot-submit"
          >
            {loading ? <Spinner size={18} className="animate-spin" /> : <EnvelopeSimple size={18} weight="bold" />}
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>
      </div>
    </div>
  );
}
